import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { VrboService } from '@app/_services/vrbo.service';
import { PropertyService } from '@app/_components/property';
import { forkJoin } from 'rxjs';
import { VRBOReviewDetailComponent } from '../vrbo-review-detail/vrbo-review-detail.component';
import { VRBOReviewRespondComponent } from '../vrbo-review-respond/vrbo-review-respond.component';
import { VRBORateGuestComponent } from '../vrbo-rate-guest/vrbo-rate-guest.component';

@Component({
    selector: 'app-vrbo-reviews',
    templateUrl: './vrbo-reviews.component.html',
    styleUrls: ['./vrbo-reviews.component.css'],
    providers: [DialogService],
    standalone: false
})
export class VRBOReviewsComponent implements OnInit {
  formLoading = false;
  reviews: any[] = [];
  filteredReviews: any[] = [];

  // Property filter
  properties: Array<{ shortTermRentalId: number; propertyCode: string }> = [];
  selectedShortTermRentalId?: number;

  // Search
  globalFilter = '';

  constructor(
    private vrboService: VrboService,
    private propertyService: PropertyService,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.formLoading = true;
    forkJoin([
      this.vrboService.fetchAllReviews(),
      this.propertyService.findAllSummary()
    ]).subscribe(
      ([reviewsRes, propRes]: any[]) => {
        const reviewsList: any[] = reviewsRes?.data?.reviews || [];

        // Build property code lookup from properties
        const props: any[] = propRes?.data?.properties || [];
        const propMap = new Map<number, string>();
        this.properties = props.map(p => {
          propMap.set(p.shortTermRentalId, p.propertyCode);
          return { shortTermRentalId: p.shortTermRentalId, propertyCode: p.propertyCode };
        });

        // Map ReviewAPI fields to display fields
        this.reviews = reviewsList.map(review => ({
          ...review,
          shortTermRentalId: Number(review.shortTermRentalId),
          propertyCode: review.propertyCode || propMap.get(Number(review.shortTermRentalId)) || '',
          titleValue: review.title || '',
          bodyValue: review.reviewDesc || '',
          reviewerName: [review.firstName, review.lastName].filter(Boolean).join(' '),
          submittedDateValue: review.reviewDate || review.createDate || '',
          hasResponse: !!review.responseDesc,
          overallRating: review.rating || 0,
          statusLabel: this.getStatusLabel(review.status)
        }));

        this.applyFilters();
        this.formLoading = false;
      },
      (err: any) => {
        this.formLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load reviews' });
      }
    );
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 1: return 'Draft';
      case 2: return 'New';
      case 3: return 'Approved';
      case 4: return 'Denied';
      default: return '';
    }
  }

  applyFilters(): void {
    let result = this.reviews;

    if (this.selectedShortTermRentalId) {
      result = result.filter(r => r.shortTermRentalId === this.selectedShortTermRentalId);
    }

    if (this.globalFilter) {
      const search = this.globalFilter.toLowerCase();
      result = result.filter(r =>
        (r.titleValue || '').toLowerCase().includes(search) ||
        (r.bodyValue || '').toLowerCase().includes(search) ||
        (r.reviewerName || '').toLowerCase().includes(search) ||
        (r.propertyCode || '').toLowerCase().includes(search)
      );
    }

    this.filteredReviews = result;
  }

  onPropertyFilterChange(): void {
    this.applyFilters();
  }

  onGlobalFilter(value: string): void {
    this.globalFilter = value;
    this.applyFilters();
  }

  onRowSelect(review: any): void {
    const ref = this.dialogService.open(VRBOReviewDetailComponent, {
      header: 'Review Details',
      width: '80%',
      data: { review, shortTermRentalId: review.shortTermRentalId }
    });

    ref.onClose.subscribe((result: any) => {
      if (result?.action === 'RESPONDED' || result?.action === 'STATUS_UPDATED') {
        this.loadData();
      }
    });
  }

  openRespondDialog(event: Event, review: any): void {
    event.stopPropagation();
    const ref = this.dialogService.open(VRBOReviewRespondComponent, {
      header: 'Respond to Review',
      width: '60%',
      data: { reviewId: review.id, shortTermRentalId: review.shortTermRentalId }
    });

    ref.onClose.subscribe((result: any) => {
      if (result?.action === 'RESPONDED') {
        this.loadData();
      }
    });
  }

  openRateGuestDialog(event: Event, review: any): void {
    event.stopPropagation();
    const supplierReservationId = this.getSupplierReservationId(review);
    const ref = this.dialogService.open(VRBORateGuestComponent, {
      header: 'Rate Guest',
      width: '60%',
      data: {
        shortTermRentalId: review.shortTermRentalId,
        reservationId: supplierReservationId,
        guestName: review.reviewerName
      }
    });

    ref.onClose.subscribe((result: any) => {
      if (result?.action === 'RATED') {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Guest rated successfully' });
      }
    });
  }

  getSupplierReservationId(review: any): string | null {
    const ids = review.reservation?.reservationIds || [];
    const supplier = ids.find((r: any) => r.idSource === 'SUPPLIER');
    return supplier?.id || null;
  }

  truncate(text: string, length: number): string {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  }
}
